import React, { useState } from 'react';
import './HowToOrder.css';

/**
 * How to Order page for JG Printing.
 */

const orderChannels = [
    {
        id: 'website',
        label: 'Via Website',
        icon: '🌐',
        steps: [
            {
                number: '01',
                title: 'Browse & Select',
                description: 'Browse our product catalog and choose the item you want printed. Click on a product to view sizing and customization options.'
            },
            {
                number: '02',
                title: 'Upload or Request a Design',
                description: 'Upload your own print-ready design file (PDF, PNG, JPG, AI at 300 DPI), or let us know you need design assistance and our team will reach out.'
            },
            {
                number: '03',
                title: 'Add to Cart & Select Preferences',
                description: 'Add your item to cart. Make sure to select your preferred options before proceeding. You can also view or remove order details from your cart at any time.'
            },
            {
                number: '04',
                title: 'Review Order & Submit for Approval',
                description: 'Review your order details carefully — quantity, size, and design. Once everything looks good, submit your order for approval.'
            },
            {
                number: '05',
                title: 'Wait for Email Confirmation',
                description: 'After submitting your order for approval, wait for an email confirmation. Once your order approval is confirmed via email, please proceed to checkout.'
            },
            {
                number: '06',
                title: 'Checkout & Choose Delivery',
                description: 'Complete your order through checkout. Payment via GCash or Cash on Delivery (COD). For delivery, choose between store pickup, Kuya (rider), or J&T Express.'
            },
        ]
    },
    {
        id: 'facebook',
        label: 'Via Facebook',
        icon: '📘',
        steps: [
            {
                number: '01',
                title: 'Message Us',
                description: 'Send us a message on Facebook at facebook.com/jgdigitalprinting. Let us know what product you\'re looking for and your estimated quantity.'
            },
            {
                number: '02',
                title: 'Get a Quote',
                description: 'We\'ll respond with pricing based on your product, size, and quantity. Bulk orders enjoy bigger discounts — just ask!'
            },
            {
                number: '03',
                title: 'Send Your Design',
                description: 'Send your design file via Messenger, or tell us you need help with the design. We offer design services for an additional fee.'
            },
            {
                number: '04',
                title: 'Approve the Proof',
                description: 'We\'ll send you a digital proof to review. Reply with your approval or any revisions before we proceed.'
            },
            {
                number: '05',
                title: 'Confirm Your Order',
                description: 'Once the proof is approved, we\'ll confirm your order details and discuss payment and delivery arrangements with you directly.'
            },
            {
                number: '06',
                title: 'Receive Your Order',
                description: 'We\'ll update you once your order is ready for pickup or dispatch. More details on this process will be provided — stay tuned!'
            },
        ]
    },
    {
        id: 'walk-in',
        label: 'Walk-In',
        icon: '🏪',
        steps: [
            {
                number: '01',
                title: 'Visit the Shop',
                description: 'Come by our shop at #16 Camatchile Street Purok 4 Lower Bicutan Taguig City No appointment needed for walk-in consultations.'
            },
            {
                number: '02',
                title: 'Discuss Your Order',
                description: 'Talk to our team directly about what you need — product type, quantity, size, and design. We\'ll walk you through all available options.'
            },
            {
                number: '03',
                title: 'Submit Your Design',
                description: 'Bring your design on a USB drive or send it via Messenger on the spot. If you need a design made, we can discuss that too.'
            },
            {
                number: '04',
                title: 'Approve the Proof',
                description: 'We\'ll show you a digital proof on-screen or send it to you. Give your approval so we can move to production.'
            },
            {
                number: '05',
                title: 'Confirm Payment',
                description: 'We\'ll confirm payment arrangements with you at the shop. More details on the walk-in payment process will be provided — stay tuned!'
            },
            {
                number: '06',
                title: 'Pick Up Your Order',
                description: 'We\'ll contact you when your order is ready. Come back to the shop and take your prints home!'
            },
        ]
    },
];

const tips = [
    {
        icon: '📐',
        title: 'File Requirements',
        description: 'Submit designs in PDF, PNG, JPG, or AI format at 300 DPI minimum, using CMYK color mode for accurate print colors.'
    },
    {
        icon: '📦',
        title: 'Bulk Orders',
        description: 'Maximum quantity of 50 pieces per order. You may place multiple orders if you need more than 50 pieces. '
    },
    {
        icon: '⚡',
        title: 'Rush Orders',
        description: 'Need it fast? Rush orders may be available for an additional fee. Contact us in advance to check availability.'
    },
    {
        icon: '🎨',
        title: 'Design Help',
        description: 'Not sure about your design? Our in-house team can create or refine your artwork for an additional design fee.'
    },
];

function HowToOrder() {
    const [activeChannel, setActiveChannel] = useState('website');
    const active = orderChannels.find(c => c.id === activeChannel);

    return (
        <main className="hto-page">

            {/* ── Hero ── */}
            <section className="hto-hero">
                <div className="hto-hero-inner">
                    <p className="hto-eyebrow">Get Started</p>
                    <h1 className="hto-title">How to Order</h1>
                    <p className="hto-subtitle">
                        Ordering from JG Printing is easy. Choose the channel that works
                        best for you and follow the steps below — we'll guide you through
                        every part of the process.
                    </p>
                </div>
                <div className="hto-hero-divider" />
            </section>

            {/* ── Channel Tabs ── */}
            <section className="hto-steps-section">
                <div className="hto-container">
                    <div className="hto-tabs">
                        {orderChannels.map(channel => (
                            <button
                                key={channel.id}
                                className={`hto-tab ${activeChannel === channel.id ? 'hto-tab--active' : ''}`}
                                onClick={() => setActiveChannel(channel.id)}
                            >
                                <span className="hto-tab-icon">{channel.icon}</span>
                                {channel.label}
                            </button>
                        ))}
                    </div>

                    {/* Steps Grid */}
                    <div className="hto-steps-grid">
                        {active.steps.map((step, i) => (
                            <div key={i} className="hto-step">
                                <div className="hto-step-top">
                                    <span className="hto-step-number">{step.number}</span>
                                    {i < active.steps.length - 1 && (
                                        <span className="hto-step-connector" aria-hidden="true" />
                                    )}
                                </div>
                                <h3 className="hto-step-title">{step.title}</h3>
                                <p className="hto-step-desc">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Tips ── */}
            <section className="hto-tips">
                <div className="hto-container">
                    <div className="hto-tips-header">
                        <span className="hto-section-tag">Good to Know</span>
                        <h2 className="hto-section-heading">Tips before you order.</h2>
                    </div>
                    <div className="hto-tips-grid">
                        {tips.map((tip, i) => (
                            <div key={i} className="hto-tip-card">
                                <span className="hto-tip-icon">{tip.icon}</span>
                                <h3 className="hto-tip-title">{tip.title}</h3>
                                <p className="hto-tip-desc">{tip.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="hto-cta">
                <div className="hto-cta-inner">
                    <p className="hto-cta-label">Ready to order?</p>
                    <h2 className="hto-cta-heading">Let's make something great.</h2>
                    <div className="hto-cta-buttons">
                        <a
                            href="https://www.facebook.com/jgdigitalprinting"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hto-cta-btn hto-cta-btn--primary"
                        >
                            Message Us on Facebook
                        </a>
                        <a
                            href="/clothing"
                            className="hto-cta-btn hto-cta-btn--secondary"
                        >
                            Browse Products
                        </a>
                    </div>
                </div>
            </section>

        </main>
    );
}

export default HowToOrder;